import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Check, Sparkles, Shield, Zap, Clock, Globe } from "lucide-react";
import cosmicBanner from "@/assets/cosmic-banner.webp";
import cosmicBannerSm from "@/assets/cosmic-banner-sm.webp";
import { useStripeLink } from "@/hooks/useStripeLink";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const PricingSection = () => {
  const { buildLink } = useStripeLink();

  return (
    <section id="pricing" className="relative z-10 py-24">
      <div className="container mx-auto px-4">
        {/* Cosmic Banner */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16 rounded-2xl overflow-hidden border border-primary/20 cosmic-glow"
        >
          <img
            src={cosmicBanner}
            srcSet={`${cosmicBannerSm} 700w, ${cosmicBanner} 1179w`}
            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 80vw, 1024px"
            alt="Unlock the full power of the cosmos"
            className="w-full h-auto object-cover"
            width={1280}
            height={720}
            loading="lazy"
          />
        </motion.div>

        {/* Heading */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl md:text-5xl font-bold cosmic-text-glow uppercase tracking-wide">
            Choose Your Path
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Unlock the full power of the cosmos and access deeper astrology insights
          </p>
          <p className="mt-3 text-cosmic-gold font-display text-sm font-semibold">
            ✨ Join Thousands Exploring Their Cosmic Path With AI ✨
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
          {/* Free */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay: 0 }}
            className="cosmic-card rounded-2xl p-8 flex flex-col border border-border/50"
          >
            <h3 className="font-display text-2xl font-bold uppercase tracking-wider text-center">
              Free
            </h3>
            <p className="text-sm text-muted-foreground mt-1 text-center">
              Start exploring the stars
            </p>
            <div className="mt-6 text-center">
              <span className="text-5xl font-bold">£0</span>
              <span className="text-muted-foreground text-sm"> /month</span>
            </div>
            <ul className="mt-8 space-y-4 flex-1">
              {[
                "Daily Horoscope",
                "Compatibility Checker",
                "Lucky Day",
                "3 AI Astrologer questions per day",
              ].map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link
              to="/signup"
              className="mt-8 text-center rounded-full py-3.5 font-semibold text-sm transition-all duration-300 border border-border hover:border-primary/50 hover:bg-primary/5 block"
            >
              Get Started
            </Link>
          </motion.div>

          {/* Premium */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="cosmic-card rounded-2xl p-8 flex flex-col border-2 border-primary/60 cosmic-glow relative"
          >
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="flex items-center gap-1.5 bg-primary text-primary-foreground text-xs font-bold px-5 py-1.5 rounded-full uppercase tracking-wider">
                <Crown className="w-3.5 h-3.5" /> Most Popular
              </div>
            </div>
            <h3 className="font-display text-2xl font-bold uppercase tracking-wider text-center text-primary mt-2">
              Premium
            </h3>
            <p className="text-sm text-muted-foreground mt-1 text-center">
              Go Deeper with Astrology
            </p>
            <div className="mt-6 text-center">
              <span className="text-5xl font-bold text-primary">£10.99</span>
              <span className="text-muted-foreground text-sm"> /month</span>
            </div>
            <div className="mt-3 text-center">
              <span className="inline-block text-xs font-semibold border border-primary/30 rounded-full px-4 py-1.5 text-primary bg-primary/5">
                3-Day Free Trial · Cancel Anytime
              </span>
            </div>
            <ul className="mt-8 space-y-4 flex-1">
              {[
                "Unlimited AI Astrologer Questions",
                "Tarot Readings",
                "Birth Chart Analysis",
                "Soulmate Finder",
                "Love Predictor",
                "Yearly Predictions",
                "Lucky Day + Compatibility",
              ].map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-primary shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <a
              href={buildLink("https://buy.stripe.com/8x23cv4NS7968zGaNM5c404")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 text-center rounded-full py-3.5 font-semibold text-sm transition-all duration-300 btn-cosmic block"
            >
              Subscribe
            </a>
          </motion.div>

          {/* VIP */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="cosmic-card rounded-2xl p-8 flex flex-col border border-cosmic-gold/40"
          >
            <h3 className="font-display text-2xl font-bold uppercase tracking-wider text-center text-cosmic-gold">
              VIP Cosmic
            </h3>
            <p className="text-sm text-muted-foreground mt-1 text-center">
              Unlock Everything
            </p>
            <div className="mt-6 text-center">
              <span className="text-5xl font-bold text-cosmic-gold">£20</span>
              <span className="text-muted-foreground text-sm"> /month</span>
            </div>
            <p className="text-xs text-cosmic-gold font-semibold mt-6 mb-3">
              Everything in Premium plus:
            </p>
            <ul className="space-y-3">
              {[
                "Deep Life Reports",
                "Future Timeline",
                "Manifestation Guidance",
                "Moon Phase Planner",
                "Priority AI Responses",
              ].map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Sparkles className="w-4 h-4 text-cosmic-gold shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <p className="text-xs text-cosmic-gold font-semibold mt-5 mb-3">
              VIP Spiritual Gifts — 50% Off:
            </p>
            <ul className="space-y-3 flex-1">
              {["Chakra Tree of Life", "Energy Pyramid"].map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Sparkles className="w-4 h-4 text-cosmic-gold shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <a
              href={buildLink("https://buy.stripe.com/7sYfZha8c8daeY4cVU5c406")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 text-center rounded-full py-3.5 font-semibold text-sm transition-all duration-300 border border-cosmic-gold/50 text-cosmic-gold hover:bg-cosmic-gold/10 block"
            >
              Go VIP
            </a>
          </motion.div>
        </div>

        {/* Trust Bar */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-12 max-w-4xl mx-auto cosmic-card rounded-full py-4 px-8 flex flex-wrap items-center justify-center gap-6 md:gap-10 text-sm text-muted-foreground"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            <span>Secure Checkout with <span className="font-bold text-foreground">Stripe</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            <span>Instant Access</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <span>Cancel Anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-cosmic-gold" />
            <span>Trusted by Members Worldwide</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
